/**
 * /admin/venues/:venueId — one venue, from the platform's side.
 *
 * This is the approval console: the page where a pending venue becomes a live
 * one, a trial gets extended, and a venue that has left the platform is
 * eventually erased.
 *
 * THE TRANSITION BUTTONS ARE DRIVEN BY THE SERVER, not by a copy of the
 * lifecycle table kept here. GET returns venue.allowedTransitions straight off
 * BILLING_TRANSITIONS, so this page cannot offer a move the endpoint will
 * refuse, and a change to the lifecycle does not have to be made twice. All
 * this file owns is what each move is CALLED, which is a presentation concern
 * and belongs on this side.
 *
 * Note the consequence for `cancelled`: its transition list is empty, so a
 * cancelled venue correctly grows no buttons at all. Cancellation is terminal
 * and the only thing left to do with it is delete it.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiDel, apiGet, apiPut } from '../lib/api.js';
import { formatStampDate, formatDateShort } from '../lib/format.js';
import { useAuth } from '../context/AuthContext.jsx';
import BillingBadge, { STATUS_LABELS } from '../components/BillingBadge.jsx';

/** How long a trial runs by default. Matches DEFAULT_TRIAL_DAYS server-side. */
const TRIAL_DAYS = 30;

const isoDate = (date) => date.toISOString().slice(0, 10);

/**
 * What a transition is called depends on where it starts.
 *
 * pending -> active is an APPROVAL; suspended -> active is a REACTIVATION; and
 * trial -> active is neither, it is the venue starting to pay. One label for
 * the destination status would get all three wrong somewhere.
 */
function transitionLabel(from, to) {
  if (to === 'cancelled') return 'Cancel venue';
  if (to === 'suspended') return 'Suspend';

  if (from === 'pending') {
    return to === 'trial' ? 'Approve (trial)' : 'Approve (active)';
  }

  if (to === 'trial')  return 'Start trial';
  return from === 'suspended' ? 'Reactivate' : 'Activate';
}

/**
 * Moves that must not happen on one click.
 *
 * `trial` is here not because it is destructive but because it needs dates
 * before it can be sent — it opens the same panel for a different reason.
 */
const NEEDS_PANEL = new Set(['trial', 'suspended', 'cancelled']);

const CONFIRM_PROMPTS = {
  suspended: 'Suspend this venue? It becomes read-only immediately and the owner is emailed.',
  cancelled: 'Cancel this venue? This is final — a cancelled venue cannot return to any other status.',
};

function InfoPair({ label, children }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** The delete box. Only mounted when deletion is actually possible. */
function DeleteVenue({ venue, onDeleted }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Exact match, untrimmed. The point of the pattern is that the name has to be
  // read off the page and typed out deliberately.
  const matches = typed === venue.name;

  const onDelete = async () => {
    setBusy(true);
    setError(null);

    const { data, error: err } = await apiDel(`/api/admin/venues/${venue.id}`, {
      confirmDelete: true,
    });

    setBusy(false);

    if (err) { setError(err); return; }
    onDeleted(data);
  };

  return (
    <section className="hd-admin-danger hd-stack">
      <h2 className="hd-section-title">Delete venue</h2>

      <p className="hd-muted">
        This permanently removes the venue and all its data. This cannot be undone.
      </p>

      <div className="hd-field">
        <label className="hd-label" htmlFor="confirm-name">
          Type <strong>{venue.name}</strong> to confirm
        </label>
        <input
          id="confirm-name"
          className="hd-input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
        />
      </div>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <div className="hd-row">
        <button
          className="hd-btn hd-btn-danger"
          type="button"
          onClick={onDelete}
          disabled={!matches || busy}
        >
          {busy ? 'Deleting…' : 'Delete this venue permanently'}
        </button>
      </div>
    </section>
  );
}

export default function PlatformVenueDetail() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [state, setState] = useState({ status: 'loading' });
  const [panel, setPanel] = useState(null);      // the status a panel is open for
  const [trial, setTrial] = useState({ start: '', end: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    const { data, error: err, status } = await apiGet(`/api/admin/venues/${venueId}`);

    if (err) return { status: 'error', error: err, notFound: status === 404 };
    return { status: 'ready', ...data };
  }, [venueId]);

  /**
   * Takes a loaded payload and seeds BOTH the page and the trial editor.
   *
   * The seeding is the part that matters: the trial inputs are controlled, and
   * starting them empty would make an untouched "Save trial dates" post two
   * nulls and wipe the window it was displaying.
   */
  const receive = useCallback((next) => {
    setState(next);
    setTrial({
      start: next.venue?.trialStartDate ?? '',
      end:   next.venue?.trialEndDate   ?? '',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    load().then((next) => { if (!cancelled) receive(next); });

    return () => { cancelled = true; };
  }, [load, receive]);

  /** Sends one status move, then re-reads the venue. */
  const apply = async (billingStatus, extra = {}) => {
    setBusy(true);
    setError(null);
    setNotice(null);

    const { data, error: err } = await apiPut(`/api/admin/venues/${venueId}`, {
      billingStatus,
      ...extra,
    });

    setBusy(false);

    if (err) { setError(err); return; }

    setPanel(null);

    // `notified` comes back from the endpoint rather than being assumed: only
    // some transitions email the owner, and a placeholder address is skipped.
    setNotice(
      `Status changed to ${STATUS_LABELS[data?.venue?.billingStatus] ?? billingStatus}.`
      + (data?.notified ? ' The owner has been emailed.' : '')
    );

    receive(await load());
  };

  /** Saves trial dates on a venue already in trial, with no status change. */
  const saveTrialDates = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);

    const { error: err } = await apiPut(`/api/admin/venues/${venueId}`, {
      trialStartDate: trial.start || null,
      trialEndDate:   trial.end   || null,
    });

    setBusy(false);

    if (err) { setError(err); return; }

    setNotice('Trial dates updated.');
    receive(await load());
  };

  const openPanel = (to) => {
    setError(null);
    setNotice(null);

    // The default window is filled in when the panel opens rather than held in
    // state from mount, so a page left open overnight still defaults to today.
    if (to === 'trial') {
      const now = new Date();
      setTrial({
        start: isoDate(now),
        end:   isoDate(new Date(now.getTime() + TRIAL_DAYS * 86400000)),
      });
    }

    setPanel(to);
  };

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <p className="hd-meta"><Link to="/admin/venues">← All venues</Link></p>
        <h1 className="hd-page-title">
          {state.notFound ? 'Venue not found' : 'Venue unavailable'}
        </h1>
        <p className="hd-muted">
          {state.notFound ? `No venue with id ${venueId}.` : state.error}
        </p>
      </div>
    );
  }

  const { venue, owner, counts } = state;
  const transitions = venue.allowedTransitions ?? [];

  // DELETE is platform_owner only, a rank above the rest of this page — a super
  // admin who sees the box would type the name and collect a 403.
  const canDelete = venue.billingStatus === 'cancelled'
                 && user?.platformRole === 'platform_owner';

  return (
    <div className="hd-stack">
      <div>
        <p className="hd-meta"><Link to="/admin/venues">← All venues</Link></p>

        <div className="hd-row">
          <h1 className="hd-page-title">{venue.name}</h1>
          <BillingBadge status={venue.billingStatus} />
        </div>

        <p className="hd-meta">
          {venue.active ? 'Live — tournaments can be run' : 'Dark — read-only'}
        </p>
      </div>

      {/* ---- Details -------------------------------------------------------- */}

      <section>
        <h2 className="hd-section-title">Venue</h2>

        <dl className="hd-admin-info">
          <InfoPair label="Slug">
            <Link to={`/venues/${venue.slug}`}>/venues/{venue.slug}</Link>
          </InfoPair>

          <InfoPair label="Owner">
            {owner
              ? (
                <>
                  {owner.name}{' '}
                  <span className="hd-muted">&lt;{owner.email}&gt;</span>
                  {owner.emailIsPlaceholder && (
                    <span className="hd-guest-tag">placeholder address — email will not reach them</span>
                  )}
                </>
              )
              : <span className="hd-muted">No owner on record</span>}
          </InfoPair>

          <InfoPair label="Created">{formatStampDate(venue.createdAt) ?? '—'}</InfoPair>
          <InfoPair label="Players">{counts?.players ?? 0}</InfoPair>
          <InfoPair label="Seasons">{counts?.seasons ?? 0}</InfoPair>
          <InfoPair label="Tournaments">{counts?.tournaments ?? 0}</InfoPair>

          {(venue.trialStartDate || venue.trialEndDate) && (
            <>
              <InfoPair label="Trial starts">
                {formatDateShort(venue.trialStartDate) ?? '—'}
              </InfoPair>
              <InfoPair label="Trial ends">
                {formatDateShort(venue.trialEndDate) ?? '—'}
              </InfoPair>
            </>
          )}
        </dl>
      </section>

      {/* ---- Status --------------------------------------------------------- */}

      <section className="hd-stack">
        <h2 className="hd-section-title">Venue status</h2>

        <div className="hd-row">
          <span className="hd-muted">Currently</span>
          <BillingBadge status={venue.billingStatus} />
        </div>

        {error  && <div className="hd-badge hd-badge-danger"  role="alert">{error}</div>}
        {notice && <div className="hd-badge hd-badge-success" role="status">{notice}</div>}

        {transitions.length === 0 && (
          <p className="hd-muted">
            A cancelled venue has nowhere left to go. Its data is retained read-only
            until it is deleted.
          </p>
        )}

        {panel === null && transitions.length > 0 && (
          <div className="hd-row">
            {transitions.map((to) => (
              <button
                key={to}
                type="button"
                className={`hd-btn ${to === 'cancelled' ? 'hd-btn-danger' : 'hd-btn-secondary'}`}
                disabled={busy}
                onClick={() => (NEEDS_PANEL.has(to) ? openPanel(to) : apply(to))}
              >
                {transitionLabel(venue.billingStatus, to)}
              </button>
            ))}
          </div>
        )}

        {/* One panel, two jobs: dates for a trial, a question for the two moves
            that should never happen on a single click. */}
        {panel !== null && (
          <div className="hd-card hd-admin-form hd-stack">
            {panel === 'trial' ? (
              <>
                <p>{transitionLabel(venue.billingStatus, 'trial')} — set the trial window.</p>

                <div className="hd-fields">
                  <div className="hd-field">
                    <label className="hd-label" htmlFor="trial-start">Trial starts</label>
                    <input
                      id="trial-start"
                      className="hd-input"
                      type="date"
                      value={trial.start}
                      onChange={(e) => setTrial((t) => ({ ...t, start: e.target.value }))}
                    />
                  </div>

                  <div className="hd-field">
                    <label className="hd-label" htmlFor="trial-end">Trial ends</label>
                    <input
                      id="trial-end"
                      className="hd-input"
                      type="date"
                      value={trial.end}
                      onChange={(e) => setTrial((t) => ({ ...t, end: e.target.value }))}
                    />
                  </div>
                </div>
              </>
            ) : (
              <p>{CONFIRM_PROMPTS[panel]}</p>
            )}

            <div className="hd-row">
              <button
                type="button"
                className={`hd-btn ${panel === 'cancelled' ? 'hd-btn-danger' : 'hd-btn-primary'}`}
                disabled={busy}
                onClick={() => apply(
                  panel,
                  panel === 'trial'
                    ? { trialStartDate: trial.start || null, trialEndDate: trial.end || null }
                    : {}
                )}
              >
                {busy ? 'Saving…' : `Yes, ${transitionLabel(venue.billingStatus, panel).toLowerCase()}`}
              </button>

              <button
                type="button"
                className="hd-btn hd-btn-quiet"
                disabled={busy}
                onClick={() => setPanel(null)}
              >
                Back
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ---- Trial dates on a running trial ---------------------------------
        * Separate from the approval panel above: extending a trial that is
        * already running is the thing that will be done most often, and there is
        * no status change involved to hang it off. */}

      {venue.billingStatus === 'trial' && panel === null && (
        <section className="hd-stack">
          <h2 className="hd-section-title">Trial window</h2>

          <div className="hd-admin-form hd-stack">
            <div className="hd-fields">
              <div className="hd-field">
                <label className="hd-label" htmlFor="trial-start-edit">Trial starts</label>
                <input
                  id="trial-start-edit"
                  className="hd-input"
                  type="date"
                  value={trial.start}
                  onChange={(e) => setTrial((t) => ({ ...t, start: e.target.value }))}
                />
              </div>

              <div className="hd-field">
                <label className="hd-label" htmlFor="trial-end-edit">Trial ends</label>
                <input
                  id="trial-end-edit"
                  className="hd-input"
                  type="date"
                  value={trial.end}
                  onChange={(e) => setTrial((t) => ({ ...t, end: e.target.value }))}
                />
              </div>
            </div>

            <div className="hd-row">
              <button
                className="hd-btn hd-btn-secondary"
                type="button"
                disabled={busy}
                onClick={saveTrialDates}
              >
                Save trial dates
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ---- Delete ---------------------------------------------------------- */}

      {canDelete && (
        <DeleteVenue
          venue={venue}
          onDeleted={() => navigate('/admin/venues')}
        />
      )}
    </div>
  );
}
